<template>
    <table class="table table-condensed table-striped">
        <thead>
            <tr>
                <th class="col-xs-2 text-center">
                    <input type="checkbox" v-model="selectAll">
                </th>

                <th class="col-xs-10">
                    {{ headline }}
                </th>

                <th class="col-xs-4">
                    Portal
                </th>

                <th class="col-xs-4">
                    Price
                </th>

                <th class="col-xs-4">
                    Status
                </th>
            </tr>
        </thead>

        <subscription
                v-for="subscription in subscriptions"
                :key="subscription.id"
                :subscription="subscription"
                :selected="selected"
                :selected-buyables="selectedBuyables"
                @add-subscription="addSubscription"
                @remove-subscription="removeSubscription"
                @add-subscription-period="addBuyable"
                @remove-subscription-period="removeBuyable"
        ></subscription>

        <order
                v-for="order in orders"
                :key="order.id"
                :order="order"
                :month="month"
                :disable-invoiced-ads="disableInvoicedAds"
                :disable-unpublished-ads="disableUnpublishedAds"
                :selected="selected"
                :selected-buyables="selectedBuyables"
                @add-order="addOrder"
                @remove-order="removeOrder"
                @add-ad="addBuyable"
                @remove-ad="removeBuyable"
        ></order>

        <total-value
                v-if="subscriptions.length || orders.length"
                :buyables="selectedBuyables"
        ></total-value>

        <tbody v-if="!subscriptions.length && !orders.length">
            <tr>
                <td class="text-center" colspan="5">
                    <i>
                        There is nothing to select.
                    </i>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
    import moment from 'moment'
    import Order from '@/components/BuyableSelect/Order'
    import Subscription from '@/components/BuyableSelect/Subscription'
    import TotalValue from '@/components/BuyableSelect/TotalValue'

    export default {
        components: {Order, Subscription, TotalValue},

        model: {
            prop: 'selectedBuyables',
            event: 'input'
        },

        props: {
            headline: {
                default: 'Order'
            },

            month: {},

            disableInvoicedAds: {
                default: true
            },

            disableUnpublishedAds: {
                default: true
            },

            selectedBuyables: {},

            subscriptions: {
                default() {
                    return []
                }
            },

            orders: {
                default() {
                    return []
                }
            }
        },

        data() {
            return {
                selectAll: false,
                selected: []
            }
        },

        watch: {
            selectAll(value) {
                value ? this.addAll() : this.removeAll()
            }
        },

        computed: {
            ads() {
                return this.orders
                    .reduce((ads, order) => {
                        return ads.concat(order.ads)
                    }, [])
                    .filter(ad => !this.isDisabled(ad))
            },

            subscriptionPeriods() {
                return this.subscriptions.reduce((subscriptionPeriods, subscription) => {
                    return subscriptionPeriods.concat(subscription.subscription_periods)
                }, []);
            },

            buyables() {
                return this.subscriptionPeriods.concat(this.ads)
            }
        },

        methods: {
            addAll() {
                this.selected = this.subscriptions.concat(this.orders)

                this.emit(this.buyables)
            },

            removeAll() {
                this.selected = []

                this.emit([])
            },

            addSubscription(subscription) {
                this.selected.push(subscription)

                this.emit(this.addBuyablesToSelected(subscription.subscription_periods))
            },

            removeSubscription(subscription) {
                this.selected.splice(this.selected.findIndex(existing => existing === subscription), 1)

                this.emit(this.removeBuyablesFromSelected(subscription.subscription_periods))
            },

            addOrder(order) {
                this.selected.push(order)

                this.emit(this.addBuyablesToSelected(order.ads.filter(ad => !this.isDisabled(ad))))
            },

            removeOrder(order) {
                this.selected.splice(this.selected.findIndex(existing => existing === order), 1)

                this.emit(this.removeBuyablesFromSelected(order.ads))
            },

            removeBuyablesFromSelected(buyables) {
                return this.selectedBuyables.filter(buyable => {
                    return buyables.findIndex(existing => existing === buyable) === -1
                })
            },

            addBuyablesToSelected(buyables) {
                return this.removeBuyablesFromSelected(buyables).concat(buyables)
            },

            addBuyable(buyable) {
                this.emit(this.selectedBuyables.concat([buyable]))
            },

            removeBuyable(buyable) {
                this.emit(this.selectedBuyables.filter(existing => existing !== buyable))
            },

            emit(value) {
                this.$emit('input', value)
            },

            getAdStatus(ad) {
                if (ad.status === 'invoiced') {
                    return 'invoiced'
                }

                if (!this.isDue(ad)) {
                    return 'not-due'
                }

                if (!this.isPublished(ad)) {
                    return 'not-published'
                }

                return ad.status
            },

            isDue(ad) {
                if (!this.month) {
                    return true
                }

                return moment(ad.starts_at).format('YYYY-MM') === this.month
            },

            isPublished(ad) {
                return moment(ad.starts_at).isBefore()
            },
            
            isDisabled(ad) {
                return !this.isDue(ad) || (this.getAdStatus(ad) === 'invoiced' && this.disableInvoicedAds) || (this.getAdStatus(ad) === 'not-published' && this.disableUnpublishedAds)
            }
        }
    }
</script>
